// Hook personalizado para gestión de comunidades/grupos
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import client from '../api/client'

// Hook que encapsula operaciones CRUD de comunidades
export function useCommunities() {
  const queryClient = useQueryClient()

  // Obtener todas las comunidades del usuario
  const communitiesQuery = useQuery({
    queryKey: ['communities'],
    queryFn: async () => {
      const response = await client.get('/communities')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutos de cache
  })

  // Obtener una comunidad específica con su tabla de posiciones
  const useCommunity = (communityId) =>
    useQuery({
      queryKey: ['community', communityId],
      queryFn: async () => {
        const response = await client.get(`/communities/${communityId}/rankings`)
        return response.data
      },
      enabled: !!communityId,
    })

  // Crear una nueva comunidad
  const createCommunity = useMutation({
    mutationFn: async (communityData) => {
      const response = await client.post('/communities', communityData)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communities'] })
    },
  })

  // Unirse a una comunidad existente con código de invitación
  const joinCommunity = useMutation({
    mutationFn: async (inviteCode) => {
      const response = await client.post('/communities/join', { codigo: inviteCode })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communities'] })
    },
  })

  // Salir de una comunidad
  const leaveCommunity = useMutation({
    mutationFn: async (communityId) => {
      const response = await client.delete(`/communities/${communityId}/leave`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communities'] })
    },
  })

  // Obtener tabla de posiciones general
  const leaderboardQuery = useQuery({
    queryKey: ['leaderboard'],
    queryFn: async () => {
      const response = await client.get('/rankings')
      return response.data
    },
    staleTime: 2 * 60 * 1000, // 2 minutos de cache
  })

  return {
    communities: communitiesQuery.data?.datos || [],
    isLoadingCommunities: communitiesQuery.isLoading,
    communitiesError: communitiesQuery.error,
    useCommunity,
    createCommunity,
    joinCommunity,
    leaveCommunity,
    leaderboard: leaderboardQuery.data?.datos || [],
    isLoadingLeaderboard: leaderboardQuery.isLoading,
    refetchCommunities: communitiesQuery.refetch,
  }
}

export default useCommunities
